/**
 * Copybook Usage Parser
 *
 * Transforms copybook_usage data from cross_reference.json into graph format.
 *
 * Input format:
 * {
 *   "copybook_usage": {
 *     "CUSTMGR.cbl": ["CUSTDATA.cpy", "DATEUTIL.cpy"],
 *     "BILLING.cbl": ["CUSTDATA.cpy", "BILLREC.cpy"]
 *   }
 * }
 *
 * Output format:
 * {
 *   nodes: [{ id, type, data: { label, nodeType, usageCount } }],
 *   edges: [{ id, source, target, type, label }]
 * }
 */

/**
 * Parse copybook usage data into graph nodes and edges
 */
export function parseCopybookUsage(data) {
  const nodes = []
  const edges = []
  const nodeMap = new Map() // Track unique nodes
  const copybookUsageCount = new Map() // Track how many programs use each copybook

  // Handle different input formats
  let copybookUsage = null

  if (data?.copybook_usage) {
    copybookUsage = data.copybook_usage
  } else if (data?.cross_reference?.copybook_usage) {
    copybookUsage = data.cross_reference.copybook_usage
  } else {
    console.warn('No copybook_usage data found')
    return { nodes: [], edges: [] }
  }

  // First pass: Count copybook usage
  Object.entries(copybookUsage).forEach(([program, copybooks]) => {
    if (Array.isArray(copybooks)) {
      copybooks.forEach(copybook => {
        copybookUsageCount.set(copybook, (copybookUsageCount.get(copybook) || 0) + 1)
      })
    }
  })

  // Second pass: Create nodes and edges
  Object.entries(copybookUsage).forEach(([programName, copybooks]) => {
    // Add program node
    const programId = `program_${programName}`
    if (!nodeMap.has(programId)) {
      nodes.push({
        id: programId,
        type: 'dependency',
        position: { x: 0, y: 0 }, // Will be set by layout
        data: {
          label: programName.replace('.cbl', ''),
          sublabel: programName,
          nodeType: 'program',
        },
      })
      nodeMap.set(programId, true)
    }

    // Add copybook nodes and edges
    if (Array.isArray(copybooks)) {
      copybooks.forEach((copybookName, index) => {
        const copybookId = `copybook_${copybookName}`
        const usageCount = copybookUsageCount.get(copybookName) || 0
        const isShared = usageCount >= 3

        // Add copybook node (if not already added)
        if (!nodeMap.has(copybookId)) {
          nodes.push({
            id: copybookId,
            type: 'dependency',
            position: { x: 0, y: 0 },
            data: {
              label: copybookName.replace('.cpy', ''),
              sublabel: isShared ? `Used by ${usageCount} programs` : copybookName,
              nodeType: isShared ? 'shared' : 'copybook',
              usageCount: usageCount,
              fillColor: isShared ? '#e0af68' : '#6b8e23',
            },
          })
          nodeMap.set(copybookId, true)
        }

        // Add edge from program to copybook
        edges.push({
          id: `${programId}_to_${copybookId}`,
          source: programId,
          target: copybookId,
          type: 'smoothstep',
          label: 'COPY',
          animated: false,
          style: {
            stroke: '#30363d',
            strokeWidth: 2,
          },
        })
      })
    }
  })

  // Handle nested copybook includes (if available)
  // This would come from analyzing COPY statements within copybooks themselves
  // For now, we'll check if there's a nested_includes field
  if (data?.nested_copybook_includes || data?.cross_reference?.nested_copybook_includes) {
    const nestedIncludes = data.nested_copybook_includes || data.cross_reference.nested_copybook_includes

    Object.entries(nestedIncludes).forEach(([parentCopybook, childCopybooks]) => {
      const parentId = `copybook_${parentCopybook}`

      if (Array.isArray(childCopybooks)) {
        childCopybooks.forEach(childCopybook => {
          const childId = `copybook_${childCopybook}`

          // Add child copybook node if not exists
          if (!nodeMap.has(childId)) {
            const usageCount = copybookUsageCount.get(childCopybook) || 0
            const isShared = usageCount >= 3

            nodes.push({
              id: childId,
              type: 'dependency',
              position: { x: 0, y: 0 },
              data: {
                label: childCopybook.replace('.cpy', ''),
                sublabel: isShared ? `Used by ${usageCount} programs` : childCopybook,
                nodeType: isShared ? 'shared' : 'copybook',
                usageCount: usageCount,
                fillColor: isShared ? '#e0af68' : '#6b8e23',
              },
            })
            nodeMap.set(childId, true)
          }

          // Add edge for nested include
          edges.push({
            id: `${parentId}_includes_${childId}`,
            source: parentId,
            target: childId,
            type: 'smoothstep',
            label: 'INCLUDES',
            animated: false,
            style: {
              stroke: '#565f89',
              strokeWidth: 1.5,
              strokeDasharray: '5,5',
            },
          })
        })
      }
    })
  }

  // Detect orphan copybooks (copybooks that are never used)
  // This is informational only - orphans won't appear in the graph
  // unless they're explicitly referenced
  const allCopybooks = new Set()
  Object.values(copybookUsage).forEach(copybooks => {
    if (Array.isArray(copybooks)) {
      copybooks.forEach(cb => allCopybooks.add(cb))
    }
  })

  const orphanedCopybooks = Array.from(allCopybooks).filter(cb =>
    !copybookUsageCount.has(cb) || copybookUsageCount.get(cb) === 0
  )

  if (orphanedCopybooks.length > 0) {
    console.info(`Found ${orphanedCopybooks.length} orphaned copybooks:`, orphanedCopybooks)
  }

  return {
    nodes,
    edges,
    stats: {
      totalPrograms: Object.keys(copybookUsage).length,
      totalCopybooks: allCopybooks.size,
      sharedCopybooks: Array.from(copybookUsageCount.entries()).filter(([_, count]) => count >= 3).length,
      orphanedCopybooks: orphanedCopybooks.length,
    },
  }
}

/**
 * Parse copybook usage from cross_reference.json file content
 */
export function parseCopybookUsageFromFile(fileContent) {
  try {
    const data = typeof fileContent === 'string' ? JSON.parse(fileContent) : fileContent
    return parseCopybookUsage(data)
  } catch (err) {
    console.error('Failed to parse copybook usage data:', err)
    return { nodes: [], edges: [] }
  }
}

/**
 * Get statistics about copybook usage
 */
export function getCopybookStats(data) {
  const copybookUsageCount = new Map()
  const copybookUsage = data?.copybook_usage || data?.cross_reference?.copybook_usage || {}

  Object.values(copybookUsage).forEach(copybooks => {
    if (Array.isArray(copybooks)) {
      copybooks.forEach(copybook => {
        copybookUsageCount.set(copybook, (copybookUsageCount.get(copybook) || 0) + 1)
      })
    }
  })

  const mostUsed = Array.from(copybookUsageCount.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)

  return {
    totalPrograms: Object.keys(copybookUsage).length,
    totalCopybooks: copybookUsageCount.size,
    sharedCopybooks: Array.from(copybookUsageCount.values()).filter(count => count >= 3).length,
    mostUsedCopybooks: mostUsed.map(([name, count]) => ({ name, count })),
    averagePerProgram: copybookUsageCount.size > 0
      ? Object.values(copybookUsage).reduce((sum, cbs) => sum + (Array.isArray(cbs) ? cbs.length : 0), 0) / Object.keys(copybookUsage).length
      : 0,
  }
}
